'use client'

import React, { useState, useEffect, useRef, Suspense } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { Menu, X, ShoppingBag, Tag, Sparkles, Store, Flower2, ChevronDown, Search, ShoppingCart, PawPrint } from 'lucide-react'
import { CategoryData, EventResponse, Merchant, Promotion } from '@/services/dataTypes'
import { getBaseImageUrl, getEventsHref, getMerchantHref, getPromotionHref } from '@/constants/hooks'
import SearchBar from './SearchBar'

interface Props {
    unique_id: string; merchantData: Merchant[]; categories: CategoryData[] | any
    headerPromoMerchant: Merchant[] | null; companyDomain: string; mer_slug: string
    mer_slug_type: string; promo_slug: string; cat_slug: string; blog_url?: string
    blog_title?: string; company_logo: string | null; events: EventResponse[]; promotions: Promotion[]
    company_name?: string
}

// ── Design tokens — matched to NavbarStrip + PetCareHero palette ─────────────
const BG = '#fffaf5'          // matches PetCareHero background — seamless blend
const BORDER = '#ffe0c2'          // matches NavbarStrip border exactly
const TEXT = '#6b4226'          // matches NavbarStrip text color
const TEXT_MUTED = 'rgba(107,66,38,0.5)'
const ACCENT = '#f59e0b'          // matches PetCareHero primary orange
const ACCENT_SOFT = 'rgba(245,158,11,0.1)'
const DD_BG = '#fffaf5'          // matches PetCareHero section background
const DD_BORDER = '#ffe0c2'          // matches NavbarStrip border
const DD_HOVER = 'rgba(245,158,11,0.07)'

const Navbar = ({
    unique_id = '',
    merchantData = [],
    categories = [],
    companyDomain = '',
    mer_slug = '',
    mer_slug_type = '',
    cat_slug = '',
    blog_url = '',
    blog_title = '',
    company_logo = null,
    events = [],
    promotions = [],
    promo_slug = '',
    headerPromoMerchant = null,
    company_name = 'PetCare',
}: Props) => {
    const [mobileOpen, setMobileOpen] = useState(false)
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null)
    const [mobileExpanded, setMobileExpanded] = useState<string | null>(null)
    const [scrolled, setScrolled] = useState(false)
    const [searchOpen, setSearchOpen] = useState(false)
    const [mounted, setMounted] = useState(false)
    const dropdownTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const searchRef = useRef<HTMLDivElement>(null)
    const pathname = usePathname()
    const logo = getBaseImageUrl(companyDomain, company_logo, '')

    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 10)
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => { setMobileOpen(false); setMobileExpanded(null) }, [pathname])

    useEffect(() => {
        document.body.style.overflow = mobileOpen ? 'hidden' : ''
        return () => { document.body.style.overflow = '' }
    }, [mobileOpen])

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(e.target as Node))
                setSearchOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const navItems = [
        { name: 'Home', href: '/', icon: PawPrint, hasDropdown: false }, // Added PawPrint icon for Home
        { name: 'Categories', href: `/${cat_slug || 'category'}`, icon: Tag, hasDropdown: true },
        { name: 'Stores', href: '/all-stores/A', icon: Store, hasDropdown: true },
        { name: 'Products', href: '/all-products', icon: ShoppingBag, hasDropdown: true },
        ...(Array.isArray(promotions) && promotions.length > 0
            ? [{ name: 'Promotions', href: `/${promo_slug || 'promotions'}`, icon: Sparkles, hasDropdown: true }] : []),
        ...(Array.isArray(events) && events.length > 0
            ? [{ name: 'Events', href: '/events', icon: Flower2, hasDropdown: true }] : []),
        ...(blog_title && blog_url
            ? [{ name: blog_title, href: blog_url, icon: null, hasDropdown: false, isExternal: true }] : []),
    ]

    const isActive = (href: string) => {
        if (!mounted) return false
        return href === '/'
            ? pathname === '/'
            : pathname === href || pathname.startsWith(href.replace('/A', ''))
    }

    const open = (name: string) => { if (dropdownTimer.current) clearTimeout(dropdownTimer.current); setActiveDropdown(name) }
    const close = () => { dropdownTimer.current = setTimeout(() => setActiveDropdown(null), 150) }

    return (
        <>
            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes t18NavDrop {
                    from { opacity:0; transform:translateY(6px); }
                    to   { opacity:1; transform:translateY(0); }
                }
                .t18-dd { animation: t18NavDrop 0.18s ease; }
                .t18-dd-item {
                    display:flex; align-items:center; gap:8px;
                    padding:7px 12px; border-radius:10px;
                    font-size:13px; font-weight:500;
                    color:${TEXT_MUTED}; transition:all 0.15s;
                }
                .t18-dd-item:hover { background:${DD_HOVER}; color:${ACCENT}; }
                .t18-search-expand {
                    transition: width 0.35s cubic-bezier(0.4,0,0.2,1), opacity 0.25s ease;
                }
            ` }} />

            <header className="relative z-[100]">
                <div
                    className="transition-all duration-300"
                    style={{
                        background: BG,
                    }}
                >
                    <div className="max-w-7xl mx-auto px-6 h-[68px] flex items-center gap-6">

                        {/* ── Logo ── */}
                        <Link href="/" className="flex-shrink-0 flex items-center gap-2.5 group" aria-label="Home">
                            {company_logo ? (
                                <Image src={logo} alt="Logo" width={120} height={36}
                                    className="h-9 w-auto object-contain transition-transform group-hover:scale-105" priority />
                            ) : (
                                <span className="flex items-center gap-2 font-black text-xl" style={{ color: TEXT }}>
                                    <div className="w-9 h-9 rounded-full flex items-center justify-center"
                                        style={{ background: ACCENT }}>
                                        <PawPrint size={18} style={{ color: '#fff' }} />
                                    </div>
                                    {company_name}
                                </span>
                            )}
                        </Link>

                        {/* ── Desktop nav — centered ── */}
                        <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center"
                            aria-label="Main navigation">
                            {navItems.map(item => (
                                <div key={item.name} className="relative"
                                    onMouseEnter={() => item.hasDropdown && open(item.name)}
                                    onMouseLeave={() => item.hasDropdown && close()}>

                                    {item.hasDropdown ? (
                                        <button
                                            className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium transition-all cursor-pointer whitespace-nowrap relative group"
                                            style={{ color: isActive(item.href) ? ACCENT : TEXT_MUTED }}
                                            aria-expanded={activeDropdown === item.name}>
                                            {item.name}
                                            <ChevronDown size={12} className={`transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180' : ''}`} />
                                            {/* Active underline */}
                                            <span className="absolute bottom-0 left-4 right-4 h-[2px] rounded-full transition-all duration-200"
                                                style={{
                                                    background: ACCENT,
                                                    opacity: isActive(item.href) ? 1 : 0,
                                                    transform: isActive(item.href) ? 'scaleX(1)' : 'scaleX(0)',
                                                }} />
                                        </button>
                                    ) : (
                                        <Link href={item.href}
                                            {...(item.isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                            className="inline-flex items-center px-4 py-2 text-sm font-medium transition-all whitespace-nowrap relative"
                                            style={{ color: isActive(item.href) ? ACCENT : TEXT_MUTED }}>
                                            {item.name}
                                            {/* Active underline */}
                                            <span className="absolute bottom-0 left-4 right-4 h-[2px] rounded-full transition-all duration-200"
                                                style={{
                                                    background: ACCENT,
                                                    opacity: isActive(item.href) ? 1 : 0,
                                                }} />
                                        </Link>
                                    )}

                                    {/* Dropdown */}
                                    {item.hasDropdown && activeDropdown === item.name && (
                                        <div
                                            className="absolute top-[calc(100%+8px)] left-0 p-3 z-[1100] rounded-2xl t18-dd"
                                            style={{
                                                background: '#ffffff',
                                                border: `1px solid ${DD_BORDER}`,
                                                boxShadow: '0 20px 50px rgba(61,43,31,0.15)',
                                                minWidth: item.name === 'Categories' || item.name === 'Stores' ? '340px'
                                                    : item.name === 'Products' ? '220px'
                                                    : item.name === 'Promotions' || item.name === 'Events' ? '260px'
                                                    : '240px',
                                            }}>

                                            {item.name === 'Categories' && (
                                                <div className="space-y-1">
                                                    <div className="text-[10px] font-bold uppercase tracking-wider px-2 pb-2" style={{ color: TEXT_MUTED, borderBottom: `1px solid ${DD_BORDER}` }}>
                                                        Browse Categories
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-1">
                                                        {categories?.slice(0, 8).map((cat: any, i: number) => (
                                                            <Link key={i} href={`/${cat?.url}`} className="t18-dd-item">
                                                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: ACCENT }} />
                                                                <span className="text-xs">{cat.name}</span>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                    <Link href="/category" className="flex items-center justify-center gap-1 mt-2 pt-2 text-xs font-semibold" style={{ borderTop: `1px solid ${DD_BORDER}`, color: ACCENT }}>
                                                        View All Categories <ChevronDown size={10} className="rotate-[-90deg]" />
                                                    </Link>
                                                </div>
                                            )}
                                            {item.name === 'Stores' && (
                                                <div className="space-y-1">
                                                    <div className="text-[10px] font-bold uppercase tracking-wider px-2 pb-2" style={{ color: TEXT_MUTED, borderBottom: `1px solid ${DD_BORDER}` }}>
                                                        Popular Stores
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-1">
                                                        {merchantData?.slice(0, 6).map((m, i) => (
                                                            <Link key={i} href={getMerchantHref(m, mer_slug, mer_slug_type)} className="t18-dd-item">
                                                                <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
                                                                    style={{ background: ACCENT_SOFT, border: `1px solid ${DD_BORDER}` }}>
                                                                    <Image src={getBaseImageUrl(companyDomain, m.merchant_logo, '')} alt="" width={16} height={16} className="object-contain" />
                                                                </div>
                                                                <span className="text-xs">{m.merchant_name}</span>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                    <Link href="/all-stores/A" className="flex items-center justify-center gap-1 mt-2 pt-2 text-xs font-semibold" style={{ borderTop: `1px solid ${DD_BORDER}`, color: ACCENT }}>
                                                        View All Stores <ChevronDown size={10} className="rotate-[-90deg]" />
                                                    </Link>
                                                </div>
                                            )}
                                            {item.name === 'Products' && (
                                                <div className="space-y-1">
                                                    <div className="text-[10px] font-bold uppercase tracking-wider px-2 pb-2" style={{ color: TEXT_MUTED, borderBottom: `1px solid ${DD_BORDER}` }}>
                                                        Browse Products
                                                    </div>
                                                    <Link href="/products" className="t18-dd-item">
                                                        <ShoppingBag size={14} style={{ color: ACCENT }} />
                                                        <span className="text-xs font-bold">All Brand Products</span>
                                                    </Link>
                                                    <Link href="/all-products" className="t18-dd-item">
                                                        <Store size={14} style={{ color: ACCENT }} />
                                                        <span className="text-xs font-bold">All Products</span>
                                                    </Link>
                                                </div>
                                            )}
                                            {item.name === 'Promotions' && (
                                                <div className="space-y-1">
                                                    <div className="text-[10px] font-bold uppercase tracking-wider px-2 pb-2" style={{ color: TEXT_MUTED, borderBottom: `1px solid ${DD_BORDER}` }}>
                                                        Active Promotions
                                                    </div>
                                                    {promotions?.slice(0, 6).map((p, i) => (
                                                        <Link key={i} href={getPromotionHref(p, promo_slug)} className="t18-dd-item">
                                                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: ACCENT }} /><span className="truncate text-xs">{p.name}</span>
                                                        </Link>
                                                    ))}
                                                    <Link href={`/${promo_slug || 'promotions'}`} className="flex items-center justify-center gap-1 mt-2 pt-2 text-xs font-semibold" style={{ borderTop: `1px solid ${DD_BORDER}`, color: ACCENT }}>
                                                        View All Promotions <ChevronDown size={10} className="rotate-[-90deg]" />
                                                    </Link>
                                                </div>
                                            )}
                                            {item.name === 'Events' && (
                                                <div className="space-y-1">
                                                    <div className="text-[10px] font-bold uppercase tracking-wider px-2 pb-2" style={{ color: TEXT_MUTED, borderBottom: `1px solid ${DD_BORDER}` }}>
                                                        Upcoming Events
                                                    </div>
                                                    {events?.slice(0, 6).map((e, i) => (
                                                        <Link key={i} href={getEventsHref(e, mer_slug_type)} className="t18-dd-item">
                                                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: ACCENT }} /><span className="truncate text-xs">{e.name}</span>
                                                        </Link>
                                                    ))}
                                                    <Link href="/events" className="flex items-center justify-center gap-1 mt-2 pt-2 text-xs font-semibold" style={{ borderTop: `1px solid ${DD_BORDER}`, color: ACCENT }}>
                                                        View All Events <ChevronDown size={10} className="rotate-[-90deg]" />
                                                    </Link>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </nav>

                        {/* ── Right side: search + cart ── */}
                        <div className="hidden lg:flex items-center gap-2 flex-shrink-0 ml-auto">

                            {/* Expanding search — expands LEFT */}
                            <div ref={searchRef} className="flex items-center gap-2">
                                <div className="t18-search-expand" style={{
                                    width: searchOpen ? '240px' : '0px',
                                    opacity: searchOpen ? 1 : 0,
                                    pointerEvents: searchOpen ? 'auto' : 'none',
                                }}>
                                    <Suspense fallback={
                                        <div className="w-full h-9 rounded-full animate-pulse" style={{ background: ACCENT_SOFT }} />
                                    }>
                                        <SearchBar companyId={unique_id} mer_slug={mer_slug}
                                            slug_type={mer_slug_type} cat_slug={cat_slug} domain={companyDomain} />
                                    </Suspense>
                                </div>
                                <button
                                    onClick={() => setSearchOpen(prev => !prev)}
                                    className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-105 flex-shrink-0"
                                    style={{ background: ACCENT_SOFT, color: ACCENT }}
                                    aria-label="Toggle search">
                                    {searchOpen ? <X size={15} /> : <Search size={16} />}
                                </button>
                            </div>
                        </div>

                        {/* ── Mobile toggle ── */}
                        <button onClick={() => setMobileOpen(true)}
                            className="lg:hidden ml-auto w-9 h-9 rounded-full flex items-center justify-center transition"
                            style={{ background: ACCENT_SOFT, color: ACCENT }}
                            aria-label="Open menu">
                            <Menu size={20} />
                        </button>
                    </div>
                </div>
            </header>

            {/* ── Mobile overlay ── */}
            {mobileOpen && (
                <div className="fixed inset-0 z-[1050]"
                    style={{ background: 'rgba(107,66,38,0.35)', backdropFilter: 'blur(4px)' }}
                    onClick={() => setMobileOpen(false)} aria-hidden="true" />
            )}

            {/* ── Mobile drawer ── */}
            <div
                className={`fixed top-0 right-0 h-full w-[80vw] max-w-[320px] z-[1100] flex flex-col transition-transform duration-300 ease-out ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}
                style={{
                    background: BG,
                    borderLeft: `1px solid ${BORDER}`,
                    boxShadow: '-8px 0 40px rgba(61,43,31,0.12)',
                }}
                role="navigation" aria-label="Mobile navigation">

                {/* Drawer header */}
                <div className="flex items-center justify-between px-5 py-4 flex-shrink-0"
                    style={{ borderBottom: `1px solid ${BORDER}` }}>
                    <Link href="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2">
                        {company_logo
                            ? <Image src={logo} alt="Logo" width={90} height={28} className="h-7 w-auto object-contain" />
                            : <span className="font-black text-lg flex items-center gap-2" style={{ color: TEXT }}>
                                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: ACCENT }}>
                                    <PawPrint size={15} style={{ color: '#fff' }} />
                                </div>
                                {company_name}
                            </span>
                        }
                    </Link>
                    <button onClick={() => setMobileOpen(false)}
                        className="w-8 h-8 rounded-full flex items-center justify-center transition"
                        style={{ background: ACCENT_SOFT, color: ACCENT }}>
                        <X size={16} />
                    </button>
                </div>

                {/* Mobile search */}
                <div className="px-4 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${BORDER}` }}>
                    <Suspense fallback={<div className="w-full h-10 rounded-full animate-pulse" style={{ background: ACCENT_SOFT }} />}>
                        <SearchBar companyId={unique_id} mer_slug={mer_slug} slug_type={mer_slug_type} cat_slug={cat_slug} domain={companyDomain} />
                    </Suspense>
                </div>

                {/* Nav items */}
                <div className="flex-1 overflow-y-auto py-2">
                    {navItems.map(item => (
                        <div key={item.name} style={{ borderBottom: `1px solid ${BORDER}` }}>
                            {item.hasDropdown ? (
                                <>
                                    <button
                                        onClick={() => setMobileExpanded(mobileExpanded === item.name ? null : item.name)}
                                        className="flex items-center justify-between w-full px-5 py-3.5 text-sm font-semibold text-left bg-transparent border-none cursor-pointer"
                                        style={{ color: isActive(item.href) ? ACCENT : TEXT }}>
                                        <span className="flex items-center gap-3">
                                            {item.icon && (
                                                <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                                                    style={{ background: isActive(item.href) ? ACCENT_SOFT : 'rgba(61,43,31,0.06)' }}>
                                                    <item.icon size={15} style={{ color: isActive(item.href) ? ACCENT : TEXT_MUTED }} />
                                                </span>
                                            )}
                                            {item.name}
                                        </span>
                                        <ChevronDown size={14} style={{ color: TEXT_MUTED, flexShrink: 0 }}
                                            className={`transition-transform duration-200 ${mobileExpanded === item.name ? 'rotate-180' : ''}`} />
                                    </button>

                                    {mobileExpanded === item.name && (
                                        <div className="mx-4 mb-3 rounded-2xl overflow-hidden"
                                            style={{ background: DD_BG, border: `1px solid ${DD_BORDER}` }}>
                                            <div className="px-4 py-2.5 text-[10px] font-black uppercase tracking-wider"
                                                style={{ color: TEXT_MUTED, borderBottom: `1px solid ${DD_BORDER}` }}>
                                                {item.name}
                                            </div>
                                            <div className="p-3">
                                                {item.name === 'Categories' && (
                                                    <div className="grid grid-cols-2 gap-0.5">
                                                        {categories?.slice(0, 8).map((cat: any, i: number) => (
                                                            <Link key={i} href={`/${cat?.url}`} onClick={() => setMobileOpen(false)}
                                                                className="t18-dd-item">
                                                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ACCENT }} />
                                                                <span className="truncate text-xs">{cat.name}</span>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                )}
                                                {item.name === 'Stores' && (
                                                    <div className="grid grid-cols-2 gap-0.5">
                                                        {merchantData?.slice(0, 6).map((m, i) => (
                                                            <Link key={i} href={getMerchantHref(m, mer_slug, mer_slug_type)} onClick={() => setMobileOpen(false)}
                                                                className="t18-dd-item">
                                                                <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
                                                                    style={{ background: ACCENT_SOFT, border: `1px solid ${DD_BORDER}` }}>
                                                                    <Image src={getBaseImageUrl(companyDomain, m.merchant_logo, '')} alt="" width={16} height={16} className="object-contain" />
                                                                </div>
                                                                <span className="truncate">{m.merchant_name}</span>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                )}
                                                {item.name === 'Products' && (
                                                    <div className="flex flex-col gap-0.5">
                                                        <Link href="/products" onClick={() => setMobileOpen(false)} className="t18-dd-item">
                                                            <ShoppingBag size={14} style={{ color: ACCENT }} /> Brand Products
                                                        </Link>
                                                        <Link href="/all-products" onClick={() => setMobileOpen(false)} className="t18-dd-item">
                                                            <Store size={14} style={{ color: ACCENT }} /> All Products
                                                        </Link>
                                                    </div>
                                                )}
                                                {item.name === 'Promotions' && (
                                                    <div className="flex flex-col gap-0.5">
                                                        {promotions?.slice(0, 6).map((p, i) => (
                                                            <Link key={i} href={getPromotionHref(p, promo_slug)} onClick={() => setMobileOpen(false)} className="t18-dd-item">
                                                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ACCENT }} />
                                                                <span className="truncate">{p.name}</span>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                )}
                                                {item.name === 'Events' && (
                                                    <div className="flex flex-col gap-0.5">
                                                        {events?.slice(0, 6).map((e, i) => (
                                                            <Link key={i} href={getEventsHref(e, mer_slug_type)} onClick={() => setMobileOpen(false)} className="t18-dd-item">
                                                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: ACCENT }} />
                                                                <span className="truncate">{e.name}</span>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <Link href={item.name === 'Stores' ? '/all-stores/A' : item.href} onClick={() => setMobileOpen(false)}
                                                className="flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold"
                                                style={{ borderTop: `1px solid ${DD_BORDER}`, color: ACCENT }}>
                                                View all {item.name}
                                            </Link>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <Link href={item.href}
                                    {...(item.isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                                    onClick={() => setMobileOpen(false)}
                                    className="flex items-center gap-3 w-full px-5 py-3.5 text-sm font-semibold transition"
                                    style={{ color: isActive(item.href) ? ACCENT : TEXT }}>
                                    {item.icon && (
                                        <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                                            style={{ background: 'rgba(61,43,31,0.06)' }}>
                                            <item.icon size={15} style={{ color: TEXT_MUTED }} />
                                        </span>
                                    )}
                                    {item.name}
                                </Link>
                            )}
                        </div>
                    ))}
                </div>

                {/* Drawer footer */}
                <div className="px-4 py-4 flex-shrink-0" style={{ borderTop: `1px solid ${BORDER}` }}>
                    <Link href={`/${mer_slug}`} onClick={() => setMobileOpen(false)}
                        className="flex items-center justify-center gap-2 w-full py-3 text-sm font-bold rounded-full transition hover:opacity-90"
                        style={{ background: ACCENT, color: '#fff' }}>
                        <ShoppingCart size={15} /> Browse Deals
                    </Link>
                </div>
            </div>
        </>
    )
}

export default Navbar
